/**
 * Shape helpers for the team stats dashboard and the leaderboard tile
 * (ST-B05 / ST-B07). The backend payloads vary by endpoint, so these accept
 * the common shapes and return what the UI renders.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const unwrap = (resp) => (resp && typeof resp === "object" && "data" in resp && resp.success !== undefined ? resp.data : resp);

const firstArray = (...candidates) => candidates.find((c) => Array.isArray(c)) || [];

export const displayName = (p) => {
  if (!p) return "Player";
  const full = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return p.player_name || p.name || full || p.username || (p.user_id != null ? `Player #${p.user_id}` : "Player");
};

/** Per-player rows { player_id, player_name, matches_played, total_score, average_score, best_score }. */
export const normalizeTeamPlayerStats = (resp) => {
  const data = unwrap(resp) || {};
  const list = Array.isArray(data)
    ? data
    : firstArray(data.players, data.playerStats, data.topPerformers, data.teamStats);
  return list
    .filter((p) => p && typeof p === "object")
    .map((p) => {
      const matches = num(p.matches_played ?? p.games_played ?? p.matches);
      const total = num(p.total_score ?? p.points_scored ?? p.total_points ?? p.value);
      return {
        player_id: p.player_id ?? p.user_id ?? p.id,
        player_name: displayName(p),
        matches_played: matches,
        total_score: total,
        average_score: p.average_score != null ? num(p.average_score) : matches > 0 ? Number((total / matches).toFixed(2)) : 0,
        best_score: num(p.best_score ?? p.career_high ?? p.max_points),
      };
    });
};

const parseScore = (sd) => {
  if (!sd) return {};
  if (typeof sd === "string") {
    try {
      return JSON.parse(sd) || {};
    } catch {
      return {};
    }
  }
  return sd;
};

const total = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return num(v.total ?? v.score ?? 0);
  return num(v);
};

/**
 * Match history rows from the team's point of view:
 * { id, match_name, result: 'win'|'loss'|'draw'|'pending', opponent, team_score,
 *   opponent_score, match_date, location }.
 */
export const toTeamMatchRows = (resp, teamId) => {
  const data = unwrap(resp) || {};
  const list = Array.isArray(data) ? data : firstArray(data.matches, data.items);
  return list
    .filter((m) => m && typeof m === "object")
    .map((m) => {
      const p1Id = m.participant1Id ?? m.participant_1_id;
      const p1Type = m.participant1Type ?? m.participant_1_type;
      const isP1 = String(p1Id) === String(teamId) && (p1Type == null || p1Type === "team");
      const sd = parseScore(m.scoreData ?? m.score_data);
      const mine = total(isP1 ? sd.participant1 : sd.participant2);
      const theirs = total(isP1 ? sd.participant2 : sd.participant1);
      const winnerId = m.winnerId ?? m.winner_id;
      let result = "pending";
      if (m.status === "completed") {
        if (winnerId == null) result = "draw";
        else result = String(winnerId) === String(teamId) ? "win" : "loss";
      }
      return {
        id: m.id,
        match_name: m.matchName || m.match_name || `Match #${m.id}`,
        result,
        opponent: isP1
          ? m.participant2Name || m.participant_2_name || "Opponent"
          : m.participant1Name || m.participant_1_name || "Opponent",
        team_score: mine ?? 0,
        opponent_score: theirs,
        match_date: m.endedAt || m.ended_at || m.scheduledAt || m.scheduled_at || null,
        location: m.venue || "",
      };
    });
};

/** Leaderboard rows { user_id, name, value, games_played, wins, losses } (top `limit`). */
export const normalizeLeaderboard = (resp, limit = 5) => {
  const data = unwrap(resp);
  const list = Array.isArray(data) ? data : firstArray(data?.leaderboard, data?.topPerformers, data?.players);
  return list
    .filter((r) => r && typeof r === "object")
    .map((r) => ({
      user_id: r.user_id ?? r.id,
      name: displayName(r),
      value: num(r.value ?? r.total ?? r.points_scored ?? r.wins),
      games_played: num(r.games_played),
      wins: num(r.wins),
      losses: num(r.losses),
    }))
    .slice(0, limit);
};
